import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOpenUnsavedChangesModal, usePrevious } from 'seagull';
import { useStore } from "../../../contexts";
import StripeElementsProvider from "../../../providers/StripeElementsProvider";
import { selectors } from "../../../store/state-selectors";
import { ResponsiveProvider } from "../../contexts";
import { BrandingActionsProvider } from "../BrandingActionsContext";
import { COBStep, getIsAllowedToSIgn, PreviewState } from "../ClientOnboarding.utils";
import { useCobBranding } from "../CobBrandingContext";
import { CobConfigProvider } from "../CobConfigContext";
import { ClientOnboardingContent } from "./ClientOnboardingContent/ClientOnboardingContent";
import { getReachableInnerSteps } from "./ClientOnboardingV2.utils";
import { OptionalServicesContext, PackageSelectionContext, useOptionalServicesState, usePackageSelectionState, useTermsOfServiceStatement } from "./hooks";
import { jsx as _jsx } from "react/jsx-runtime";
export const ClientOnboardingInnerV2 = props => {
  const {
    relationship,
    currentStep,
    previewState,
    disableMobile,
    dismissPreviewModal,
    onPreviewStateChange,
    setCurrentStep,
    refresh: _refresh
  } = props;
  const [localPreviewState, setLocalPreviewState] = useState(previewState);
  const {
    isDirty,
    revertToInitial
  } = useCobBranding();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const userId = useStore(selectors.user.id);
  const role = useStore(selectors.user.role);
  const isPreview = !!previewState;
  const shouldSignStatement = getIsAllowedToSIgn({
    relationship,
    userId,
    role
  });
  const reachableSteps = useMemo(() => getReachableInnerSteps(relationship), [relationship]);
  const getStepIndex = step => reachableSteps.indexOf(step);
  const currentStepIndex = getStepIndex(currentStep);
  const [highestReachedIndex, setHighestReachedIndex] = useState(() => Math.max(currentStepIndex, 0));
  useEffect(() => {
    if (currentStepIndex > highestReachedIndex) {
      setHighestReachedIndex(currentStepIndex);
    }
  }, [currentStepIndex, highestReachedIndex]);
  const visitedSteps = useMemo(() => reachableSteps.slice(0, highestReachedIndex + 1), [reachableSteps, highestReachedIndex]);
  const prevStep = usePrevious(currentStep);
  const directionRef = useRef(1);
  const hasVisitedCoverPageRef = useRef(false);
  const isCoverPage = currentStep === COBStep.welcome;
  const wasCoverPage = prevStep === COBStep.welcome;
  if (prevStep && prevStep !== currentStep && !isCoverPage && !wasCoverPage) {
    directionRef.current = getStepIndex(currentStep) > getStepIndex(prevStep) ? 1 : -1;
  }
  const handleBack = () => {
    if (currentStepIndex === 0) {
      setCurrentStep(COBStep.welcome);
    } else {
      setCurrentStep(reachableSteps[currentStepIndex - 1]);
    }
  };
  const handleNext = useCallback(() => {
    if (currentStepIndex < reachableSteps.length - 1) {
      setCurrentStep(reachableSteps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, reachableSteps, setCurrentStep]);
  const {
    currentStatement,
    isStatementAccepted,
    isNextDisabled,
    handleStatementChange
  } = useTermsOfServiceStatement({
    relationship,
    currentStep,
    shouldSignStatement,
    isPreview
  });
  const {
    hideNextButton,
    packageSelectionContextValue,
    selectedBundleId
  } = usePackageSelectionState({
    relationship,
    currentStep,
    userId,
    handleNext
  });
  const {
    optionalServicesContextValue
  } = useOptionalServicesState({
    relationship,
    selectedBundleId
  });
  const toggleBrandSetup = useCallback(async () => {
    if (localPreviewState === PreviewState.SetupBrand) {
      if (isDirty) {
        const confirmed = await openUnsavedChangesModal({
          testid: 'client-preview-unsaved-popup',
          title: 'Unsaved brand changes'
        });
        if (confirmed) {
          revertToInitial();
          setLocalPreviewState(previewState);
          onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
        }
      } else {
        setLocalPreviewState(previewState);
        onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
      }
    } else {
      setLocalPreviewState(PreviewState.SetupBrand);
      onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(PreviewState.SetupBrand);
    }
  }, [localPreviewState, previewState, isDirty, revertToInitial, openUnsavedChangesModal, onPreviewStateChange]);
  return /*#__PURE__*/_jsx(ResponsiveProvider, {
    disableMobile: disableMobile,
    children: /*#__PURE__*/_jsx(CobConfigProvider, {
      relationship: relationship,
      previewState: localPreviewState,
      originalPreviewState: previewState,
      isPreviewModal: !!previewState,
      children: /*#__PURE__*/_jsx(BrandingActionsProvider, {
        onToggleBrandSetup: toggleBrandSetup,
        children: /*#__PURE__*/_jsx(StripeElementsProvider, {
          children: /*#__PURE__*/_jsx(PackageSelectionContext.Provider, {
            value: packageSelectionContextValue,
            children: /*#__PURE__*/_jsx(OptionalServicesContext.Provider, {
              value: optionalServicesContextValue,
              children: /*#__PURE__*/_jsx(ClientOnboardingContent, {
                relationship: relationship,
                currentStep: currentStep,
                previewState: previewState,
                localPreviewState: localPreviewState,
                dismissPreviewModal: dismissPreviewModal,
                onToggleBrandSetup: toggleBrandSetup,
                showBrandSetupButton: !!previewState,
                setCurrentStep: setCurrentStep,
                visitedSteps: visitedSteps,
                directionRef: directionRef,
                hasVisitedCoverPageRef: hasVisitedCoverPageRef,
                handleBack: handleBack,
                handleNext: handleNext,
                statement: currentStatement,
                isStatementAccepted: isStatementAccepted,
                onStatementChange: handleStatementChange,
                isNextDisabled: isNextDisabled,
                hideNextButton: hideNextButton
              })
            })
          })
        })
      })
    })
  });
};